import {
  PRINTOUT_TEMPLATE_NAME_MAX_LENGTH,
  printoutPageSettingsSchema,
  printoutTemplateBlocksSchema,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { paginated } from './common/pagination.js';
import { printoutTemplatePreviewContract } from './printout-template-preview.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const nameSchema = z.string().trim().min(1).max(PRINTOUT_TEMPLATE_NAME_MAX_LENGTH);

export const PRINTOUT_TEMPLATE_SORT_FIELDS = ['name', 'updatedAt'] as const;
export type PrintoutTemplateSortField = (typeof PRINTOUT_TEMPLATE_SORT_FIELDS)[number];

export const printoutTemplateSummarySchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  name: z.string(),
  blockCount: z.number().int().min(0),
  /** How many campaigns name it — a template in use cannot be deleted. */
  campaignCount: z.number().int().min(0),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PrintoutTemplateSummary = z.infer<typeof printoutTemplateSummarySchema>;

export const printoutTemplateSchema = printoutTemplateSummarySchema.extend({
  blocks: printoutTemplateBlocksSchema,
  settings: printoutPageSettingsSchema,
});

export type PrintoutTemplate = z.infer<typeof printoutTemplateSchema>;

export const printoutTemplateOrganizationSchema = z.object({ id: z.uuid(), name: z.string() });

export type PrintoutTemplateOrganization = z.infer<typeof printoutTemplateOrganizationSchema>;

/**
 * The organizations' libraries of queue-printout templates —
 * docs/plans/11-test-revisions.md §11.8,
 * docs/decisions/0082-a-printout-template-is-an-organizations-ordered-list-of-blocks.md.
 * **Organization scope** for reads and writes alike: a caller reaches a
 * template when their scope wholly holds its organization, the rule every row
 * that belongs to an organization follows (0045). Reads ride on
 * `campaign.view` and writes on `campaign.edit`, since a template is campaign
 * configuration a campaign's own managers lay out.
 */
export const printoutTemplateContract = {
  ...printoutTemplatePreviewContract,
  list: oc
    .route({ method: 'GET', path: '/printout-templates', summary: 'Printout templates' })
    .errors(guarded)
    .input(
      sortedListRequest(PRINTOUT_TEMPLATE_SORT_FIELDS, 'name', 'asc').extend({
        organizationId: z.uuid().optional(),
      }),
    )
    .output(paginated(printoutTemplateSummarySchema)),

  get: oc
    .route({ method: 'GET', path: '/printout-templates/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ template: printoutTemplateSchema })),

  /** The organizations the caller may keep templates for: those their scope wholly holds. */
  organizations: oc
    .route({ method: 'GET', path: '/printout-template-organizations' })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ items: z.array(printoutTemplateOrganizationSchema) })),

  create: oc
    .route({ method: 'POST', path: '/printout-templates' })
    .errors(guarded)
    .input(
      z.object({
        organizationId: z.uuid(),
        name: nameSchema,
        blocks: printoutTemplateBlocksSchema,
        settings: printoutPageSettingsSchema.optional(),
      }),
    )
    .output(z.object({ template: printoutTemplateSchema })),

  update: oc
    .route({ method: 'PATCH', path: '/printout-templates/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        name: nameSchema.optional(),
        blocks: printoutTemplateBlocksSchema.optional(),
        settings: printoutPageSettingsSchema.optional(),
      }),
    )
    .output(z.object({ template: printoutTemplateSchema })),

  /** Refused `CONFLICT` / `PRINTOUT_TEMPLATE_IN_USE` while any campaign names it. */
  delete: oc
    .route({ method: 'DELETE', path: '/printout-templates/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ id: z.uuid(), deleted: z.literal(true) })),
};
